import { useState, useEffect, useCallback, useRef } from 'react';
import WebSocketService from '../services/websocket';
import {
  aplicarAtendimentoFinalizado,
  aplicarNovaMensagem,
} from './conversasPatch';

export const useWebSocket = (token, instanciaFiltro) => {
  const [conectado, setConectado] = useState(false);
  const [conversas, setConversas] = useState([]);
  const [conversasAgrupadas, setConversasAgrupadas] = useState({});
  const [erroEnvio, setErroEnvio] = useState(null);
  // Não existia estado de carregamento: a lista ficava vazia e o contador
  // marcava "0 conversas" durante a espera.
  const [carregandoConversas, setCarregandoConversas] = useState(true);
  const reconnectTimeoutRef = useRef(null);
  const heartbeatRef = useRef(null);
  const instanciaFiltroRef = useRef(instanciaFiltro);
  
  // Manter ref atualizado
  useEffect(() => {
    instanciaFiltroRef.current = instanciaFiltro;
  }, [instanciaFiltro]);

  const agruparConversas = useCallback((conversas) => {
    console.log('🔍 Agrupando conversas:', conversas?.length, 'conversas');
    const grupos = {};
    
    conversas?.forEach((conv) => {
      // Chave única: telefone + instancia_id
      const chave = `${conv.telefone}_${conv.instancia_id || 'sem_instancia'}`;
      
      // Backend já retorna apenas a última mensagem de cada grupo, não precisa reagrupar
      grupos[chave] = {
        telefone: conv.telefone,
        ultima_conversa_id: conv.id,
        instancia_id: conv.instancia_id,
        instancia: conv.instancia,
        nome_contato: conv.nome_contato,
        foto_contato: conv.foto_contato,
        ultima_mensagem: conv.ultima_mensagem_preview || conv.resposta_enviada || conv.mensagem_recebida,
        eh_resposta: !!conv.resposta_enviada,
        respondida_por_humano: conv.respondida_por_humano,
        create_at: conv.ultima_mensagem_at || conv.update_at || conv.create_at,
        tem_pendente: !conv.respondida,
        ia_pausada: conv.ia_pausada || false,
        tags: conv.tags || [],
        atendente_id: conv.atendente_id || null,
        atendente_nome: conv.atendente_nome || null,
        status_mensagem: conv.respondida ? (conv.status_mensagem || null) : null,
        is_group: conv.is_group || false,
        group_id: conv.group_id || null,
      };
    });

    console.log('✅ Grupos criados:', Object.keys(grupos).length);
    return grupos;
  }, []);

  const conectar = useCallback(() => {
    if (!token) {
      console.log('⚠️ Sem token - não conectará WebSocket');
      return;
    }
    
    if (WebSocketService.isAtendimentoConnected) {
      console.log('⚠️ WebSocket já está conectado - não reconectará');
      return;
    }

    console.log('🔌 Iniciando conexão WebSocket Atendimento...');
    WebSocketService.connectAtendimento(token);

    WebSocketService.atendimentoSocket?.on('connect', () => {
      console.log('✅ WebSocket conectado (hook)');
      setConectado(true);
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
    });

    WebSocketService.atendimentoSocket?.on('disconnect', (reason) => {
      console.log('❌ WebSocket desconectado (hook):', reason);
      setConectado(false);
    });

    WebSocketService.onTodasConversas((conversas) => {
      setConversas(conversas || []);
      setConversasAgrupadas(agruparConversas(conversas));
      setCarregandoConversas(false);
    });

    WebSocketService.onNovaMensagem((conversa) => {
      console.log('📡 Nova mensagem no useWebSocket:', conversa);
      
      // Ignorar mensagens de outros canais quando filtro está ativo
      const filtro = instanciaFiltroRef.current;
      if (filtro && conversa.instancia_id && conversa.instancia_id !== filtro) {
        console.log('🔇 Mensagem de outro canal, ignorando');
        return;
      }
      
      // Tocar som se for mensagem recebida do cliente (não quando atendente envia)
      if (conversa.direcao === 'recebida') {
        console.log('🔔 Tentando tocar notificação...');
        
        const tocarSom = () => {
          const audio = new Audio('/notification.mp3');
          audio.volume = 0.5;
          audio.play()
            .then(() => {
              console.log('✅ Som tocado');
              window.audioHabilitado = true;
            })
            .catch(e => {
              if (!window.audioHabilitado) {
                console.log('⏳ Clique em qualquer lugar para habilitar notificações sonoras');
                // Habilitar no próximo clique
                document.addEventListener('click', () => {
                  window.audioHabilitado = true;
                  console.log('✅ Notificações habilitadas');
                }, { once: true });
              }
            });
        };
        
        tocarSom();
      }
      
      // Patch local em vez de recarregar a lista inteira. Recarrega apenas se a
      // conversa ainda não está na lista (thread nova precisa de avatar/tags).
      setConversasAgrupadas((prev) => {
        const { mapa, novaThread } = aplicarNovaMensagem(prev, conversa);
        if (novaThread) {
          WebSocketService.recarregarConversas(instanciaFiltroRef.current);
        }
        return mapa;
      });
    });

    // Este evento NÃO é redundante com nova_mensagem: o caminho de enviarResposta
    // não chama notificarNovaMensagem, então é a única notificação dele.
    // A lista NÃO é atualizada aqui: o único caminho de envio do front é
    // 'enviar_mensagem_direta', e o serviço dele já emite 'nova_mensagem' com
    // telefone, instancia_id, direcao e status_mensagem — o listener acima
    // patcheia a linha. Tratar aqui também forçava uma recarga por envio.
    WebSocketService.onMensagemEnviada(() => {});

    // Escutar conversa atribuída a atendente
    if (WebSocketService.atendimentoSocket) {
      WebSocketService.atendimentoSocket.on('conversa_atribuida', ({ telefone, instancia_id, atendente_id, atendente_nome, ia_pausada }) => {
        const chave = `${telefone}_${instancia_id || 'sem_instancia'}`;
        setConversasAgrupadas(prev => {
          if (!prev[chave]) return prev;
          return {
            ...prev,
            [chave]: {
              ...prev[chave],
              atendente_id,
              atendente_nome,
              // Só assumir_conversa pausa a IA; o emit de enviar_resposta não
              // manda o campo e não deve zerar o estado atual.
              ...(ia_pausada !== undefined && { ia_pausada }),
            },
          };
        });
      });
    }

    WebSocketService.onTagAplicada(() => {
      // O payload é { conversaId, tag } — sem telefone/instancia_id, não dá para
      // patchear com segurança. Recarga debounced; é evento de baixa frequência.
      WebSocketService.recarregarConversas(instanciaFiltroRef.current);
    });

    WebSocketService.onAtendimentoFinalizado(({ telefone }) => {
      // O patch abaixo faz exatamente o que a recarga trazia (liberar conversa e
      // reativar IA), então a recarga era redundância.
      setConversasAgrupadas((prev) => aplicarAtendimentoFinalizado(prev, telefone));
    });

    WebSocketService.onStatusAtualizado(({ telefone, status }) => {
      console.log('📊 Status atualizado via WebSocket:', telefone, status);
      
      // Atualizar status em tempo real
      setConversas(prev => {
        const updated = prev.map(conv => 
          conv.telefone === telefone && (conv.resposta_enviada || conv.direcao === 'enviada')
            ? { ...conv, status_mensagem: status }
            : conv
        );
        return updated;
      });
      
      // Atualizar conversas agrupadas também
      setConversasAgrupadas(prev => {
        const newState = { ...prev };
        for (const chave of Object.keys(newState)) {
          if (newState[chave].telefone === telefone && !newState[chave].tem_pendente) {
            newState[chave] = { ...newState[chave], status_mensagem: status };
          }
        }
        return newState;
      });
    });

    WebSocketService.onErro((error) => {
      console.error('WebSocket Error:', error.message);
      // Se a carga inicial falha (token expirado, erro de query), 'todas_conversas'
      // nunca chega e o skeleton ficaria para sempre. Só os erros de conexão
      // interessam: um envio que falha no meio da carga não deve encerrá-la.
      if (error?.code || /token|conectar/i.test(error?.message || '')) {
        setCarregandoConversas(false);
      }
      setErroEnvio(error.message);
      setTimeout(() => setErroEnvio(null), 5000);
    });
  }, [token, agruparConversas]);

  // Estável: o handler de filtro do Atendimento o usa como dep de useCallback.
  const trocarCanal = useCallback((instanciaId) => {
    setConversasAgrupadas({});
    setConversas([]);
    setCarregandoConversas(true);
    WebSocketService.recarregarConversas(instanciaId, { imediato: true });
  }, []);

  const desconectar = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }
    if (heartbeatRef.current) {
      clearInterval(heartbeatRef.current);
    }
    WebSocketService.disconnectAtendimento();
    setConectado(false);
  }, []);

  useEffect(() => {
    if (token && !WebSocketService.isAtendimentoConnected) {
      console.log('🎣 useEffect: Tentando conectar WebSocket');
      conectar();
    } else if (token && WebSocketService.isAtendimentoConnected) {
      setConectado(true);
    }
  }, [token]);

  // Desconectar só quando componente desmonta de verdade
  useEffect(() => {
    return () => {
      console.log('🧹 Componente desmontando: desconectando WebSocket');
      desconectar();
    };
  }, []);

  return {
    conectado,
    conversas,
    conversasAgrupadas,
    carregandoConversas,
    erroEnvio,
    recarregar: (instanciaId) => WebSocketService.recarregarConversas(instanciaId),
    // Troca de canal muda o conjunto de dados: sem limpar e voltar ao skeleton,
    // as linhas do canal anterior ficam na tela durante o round trip, contadas
    // no cabeçalho como se fossem do canal novo.
    trocarCanal,
  };
};